import { API_ORIGIN, apiUrl } from "./apiOrigin";
import React, { useState, useEffect, useMemo } from "react";
import { displayName, useMaskNames, MaskToggleButton } from "./nameMask";
import InvoiceAnalysis from "./invoice_analysis";
import InvoiceSuppliers from "./invoice_suppliers";

/* The variance workflow, in the order it actually happens.
 *
 * The stored values are older than the wording anyone uses out loud, and both
 * are kept: renaming the values would strand every invoice already carrying
 * one, and renaming nothing would leave the screen speaking a language the
 * pharmacy does not.
 *
 * An invoice arrives at AWAITING REFERRAL on its own, the moment a delivery
 * that disagrees with the paperwork is closed. Nothing has to be noticed for
 * that to happen, which is the point - the failure this replaces was a short
 * delivery closing and looking exactly like a clean one.
 */

/* The six states an invoice can be in, in the order the work happens.
 *
 * Mutually exclusive by construction: bucketOf returns exactly one, so an
 * invoice appears on exactly one tab and the counts add up to the total. That
 * is the whole point of tabs over filters - a filter can leave you wondering
 * whether you have seen everything, and a set of counts that sums to the total
 * cannot.
 *
 * The order matters too. It is the life of a delivery from left to right, and
 * the two that need somebody to DO something - awaiting referral, awaiting
 * resolution - sit in the middle where they are hard to walk past.
 */
const BUCKETS = [{
  key: "notStarted",
  label: "Not started",
  hint: "The paperwork is on the system and nothing has been scanned against it yet."
}, {
  key: "inProgress",
  label: "Being booked in",
  hint: "Somebody has started scanning this delivery but has not finished it."
}, {
  key: "awaitingReferral",
  label: "Variance — to refer",
  hint: "Finished, and it does not match the paperwork. Nobody has taken it to the supplier yet.",
  urgent: true
}, {
  key: "awaitingResolution",
  label: "Variance — referred",
  hint: "Raised with the supplier. Waiting on a credit, a replacement or an explanation.",
  urgent: true
}, {
  key: "resolved",
  label: "Variance — resolved",
  hint: "Settled with the supplier. Kept on the record rather than cleared away."
}, {
  key: "clean",
  label: "Completed",
  hint: "Finished and everything matched. Nothing to do."
}];

/** Exactly one bucket per invoice. */
function bucketOf(inv) {
  const done = inv.status === "complete" || inv.status === "closed";
  if (!done) {
    /* "Started" means somebody has actually touched the delivery.
     *
     * Scanning is one way. Ticking lines off against the box is the other, and
     * counting only scans put a delivery being checked in entirely by hand on
     * the "Not started" tab - the one tab that exists to show what nobody has
     * picked up yet. It would have sat there looking untouched while a branch
     * worked through it.
     */
    const touched = (inv.itemCount || 0) > 0 || (inv.scannedLineCount || 0) > 0 || (inv.handCheckedCount || 0) > 0;
    return touched ? "inProgress" : "notStarted";
  }
  switch (inv.varianceStatus) {
    case "UNRESOLVED":
      return "awaitingReferral";
    case "SUPPLIER_EMAILED":
      return "awaitingResolution";
    case "RESOLVED":
      return "resolved";
    default:
      return "clean";
  }
}
const REFERRAL_METHODS = [["phone", "Phone call"], ["email", "Email"], ["portal", "Online form / portal"], ["rep", "Told the rep or driver"], ["other", "Something else"]];
const VARIANCE = {
  UNRESOLVED: {
    label: "Awaiting referral",
    hint: "The delivery does not match the paperwork and nobody has taken it " + "to the supplier yet.",
    bg: "#fdecea",
    fg: "#a00"
  },
  SUPPLIER_EMAILED: {
    label: "Awaiting resolution",
    hint: "Raised with the supplier. Waiting on a credit, a replacement or an " + "explanation.",
    bg: "#fff4e5",
    fg: "#a05a00"
  },
  RESOLVED: {
    label: "Resolved",
    hint: "Settled. Kept on the record rather than cleared away.",
    bg: "#e8f5e9",
    fg: "#177245"
  }
};
const API = apiUrl("");

/**
 * Incoming supplier invoices, and what was actually received against them.
 *
 * Left: invoices forwarded in by branch, plus any raised on the fly in the app.
 * Right: three panels — the raw email as it arrived, the invoice lines with
 * what was scanned against each, and the raw scans for confirmation.
 *
 * Everything on the right comes from /getInvoiceReconciliation, which joins the
 * two sides at vppid: invoice lines carry a PIP code, scans carry a GTIN, and
 * both resolve through data already held. Nothing reads live stock — the
 * scanned lines are the immutable snapshot taken as stock was booked in, so
 * these figures do not move when stock is later split or destroyed.
 */
const StockInvoices = () => {
  const masked = useMaskNames();
  const [invoices, setInvoices] = useState([]);
  const [recon, setRecon] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [loadingList, setLoadingList] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [error, setError] = useState(null);
  const [pharmacyFilter, setPharmacyFilter] = useState("");
  const [supplierFilter, setSupplierFilter] = useState("");
  const [tab, setTab] = useState("awaitingReferral");
  const [search, setSearch] = useState("");
  const [emailOpen, setEmailOpen] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [pages, setPages] = useState([]);
  const [retrying, setRetrying] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  // The reading notes are folded away by default. They are long, they concern
  // how the photograph read rather than what arrived, and they were taking the
  // top third of the screen on every single invoice.
  const [problemsOpen, setProblemsOpen] = useState(false);

  /* One pack photograph, fetched only when somebody asks for it.
   *
   * The detail route says WHICH packs have one; the image itself is a separate
   * call, because a delivery can be a hundred packs and pulling every picture
   * to decide whether to draw a camera icon would make the screen unusable. */
  const [packPhoto, setPackPhoto] = useState(null);
  const openPackPhoto = async (invoiceId, itemId, label) => {
    setPackPhoto({
      loading: true,
      label
    });
    try {
      const d = await post("/api/invoiceAnalysis/itemPhoto", {
        invoiceId,
        itemId
      });
      setPackPhoto(d.photo ? {
        ...d.photo,
        label
      } : null);
      if (!d.photo) setError("That photograph is no longer stored.");
    } catch (e) {
      setPackPhoto(null);
      setError(`Could not load that photo: ${e.message}`);
    }
  };
  const [mainTab, setMainTab] = useState("invoices"); // invoices | analysis | setup
  const [branchList, setBranchList] = useState([]);
  const groupId = localStorage.getItem("userId");
  const token = localStorage.getItem("authToken");
  const post = async (path, body) => {
    const res = await fetch(`${API}${path}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify(body)
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
  };
  const loadList = async () => {
    setLoadingList(true);
    setError(null);
    try {
      // Scoped from the token, not from a groupId taken out of localStorage.
      // That value is the id of the organisation you logged in AS, so a
      // "Pharmace all" user was asking for invoices belonging to Pharmace all
      // and finding none, while the invoices sat under each branch's own
      // organisation.
      /* Everything, always.
       *
       * The tabs partition the same set, so the counts on them are only
       * truthful if every invoice is in hand - fetching completed ones only
       * when a checkbox is ticked would leave four of the six tabs reading
       * zero until somebody found the checkbox. */
      const data = await post("/api/invoiceAnalysis/list", {
        includeComplete: true
      });
      setInvoices(Array.isArray(data.invoices) ? data.invoices : []);
      setBranchList(Array.isArray(data.branches) ? data.branches : []);
    } catch (e) {
      setError(`Could not load invoices: ${e.message}`);
    } finally {
      setLoadingList(false);
    }
  };

  /* Reload when the organisation changes, and only then.
   *
   * loadList is deliberately not a dependency: it is redefined on every render,
   * so including it would refetch the whole list on every keystroke in the
   * search box. The suppression has to sit on its own line - it applies to the
   * line AFTER it, and when this was written as a one-liner the dependency
   * array was on the same line, so it suppressed nothing.
   */
  useEffect(() => {
    loadList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupId]);
  const openInvoice = async inv => {
    setSelectedId(inv._id);
    setRecon(null);
    setEmailOpen(false);
    setLoadingDetail(true);
    setError(null);
    try {
      // Same scoping as the list. Using the groupId from localStorage here
      // made the detail 404 for exactly the invoices the list had just shown:
      // the list was scoped by token, the detail by a group the browser
      // supplied, and the two disagreed about which invoices exist.
      const data = await post("/api/invoiceAnalysis/detail", {
        invoiceId: inv._id
      });
      setRecon(data);

      // Fetched separately so the panel appears immediately and the images
      // arrive behind it, rather than everything waiting on the photographs.
      setPhotos([]);
      setPages([]);
      if (data.invoice?.photos > 0 || data.invoice?.pageCount > 0) {
        post("/api/invoiceAnalysis/detail", {
          invoiceId: inv._id,
          includePhotos: true
        }).then(d => {
          setPhotos(d.photoData || []);
          setPages(d.pages || []);
        }).catch(() => {
          setPhotos([]);
          setPages([]);
        });
      }
    } catch (e) {
      setError(`Could not load that invoice: ${e.message}`);
    } finally {
      setLoadingDetail(false);
    }
  };

  /**
   * Read a photographed invoice again.
   *
   * Goes through the token-scoped route rather than /retryInvoiceExtraction,
   * which identifies the invoice by a groupId in the body - and the groupId in
   * localStorage is the organisation you logged in AS, which is the mismatch
   * that used to 404 the detail panel for invoices the list had just shown.
   *
   * The queue is asynchronous, so this does not wait for a result: it re-opens
   * the invoice, which now reports "reading", and the reader refreshes when
   * they want to see whether it landed.
   */
  const retryExtraction = async invoiceId => {
    setRetrying(true);
    try {
      await post("/api/invoiceAnalysis/retryExtraction", {
        invoiceId
      });
      const inv = invoices.find(i => i._id === invoiceId);
      if (inv) await openInvoice(inv);
    } catch (e) {
      setError(`Could not queue that invoice: ${e.message}`);
    } finally {
      setRetrying(false);
    }
  };

  /* Move an invoice along the variance workflow.
   *
   * Deliberately not automatic beyond the first step. Arriving at "awaiting
   * referral" is a fact about the delivery and the system can see it; deciding
   * that the supplier has been asked, or that the matter is settled, are things
   * a person did and only that person knows. */
  /* Ticking a line off from the office.
   *
   * Held per line key so only the row being saved shows as busy - a single
   * flag would grey out the whole grid for one line, on a screen whose whole
   * purpose is comparing lines against each other.
   */
  const [checkingLine, setCheckingLine] = useState(null);

  /**
   * "That line is settled."
   *
   * The same act the branch performs on the handset, available here because
   * the paperwork outlives the tote: the missing two arrive a week later, or a
   * credit is agreed on the phone, and the person recording it is at a desk.
   *
   * Undo where a hand figure is already on the line, so a mis-tick is one
   * click to reverse rather than a support call.
   */
  const checkOffLine = async (invoiceId, line) => {
    setCheckingLine(line.key);
    try {
      await post("/api/invoiceAnalysis/checkOffLine", {
        invoiceId,
        lineKey: line.key,
        // null undoes and hands the line back to the scan count; omitting it
        // means the full invoiced quantity.
        ...(line.receivedBy === "hand" ? {
          packs: null
        } : {})
      });
      const inv = invoices.find(i => i._id === invoiceId);
      if (inv) await openInvoice(inv);
    } catch (e) {
      setError(`Could not save that: ${e.message}`);
    } finally {
      setCheckingLine(null);
    }
  };
  const [varianceBusy, setVarianceBusy] = useState(false);
  // The referral form and the resolution box are only ever open for the
  // invoice on screen, so they hold their own draft rather than one per row.
  const [referral, setReferral] = useState(null); // {method, at, reference}
  const [resolution, setResolution] = useState("");
  const setVariance = async (invoiceId, varianceStatus, extra = {}) => {
    setVarianceBusy(true);
    try {
      await post("/api/invoiceAnalysis/setVariance", {
        invoiceId,
        varianceStatus,
        ...extra
      });
      // Both, and in this order: the open invoice so the panel redraws in its
      // new state, then the list so its badge agrees. Refreshing only one
      // leaves the two halves of the screen disagreeing about the same record.
      const inv = invoices.find(i => i._id === invoiceId);
      if (inv) await openInvoice(inv);
      await loadList();
      setReferral(null);
      setResolution("");
    } catch (e) {
      setError(`Could not change that: ${e.message}`);
    } finally {
      setVarianceBusy(false);
    }
  };
  const reparse = async invoiceId => {
    try {
      await post("/invoiceReparse", {
        invoiceId
      });
      await loadList();
      const inv = invoices.find(i => i._id === invoiceId);
      if (inv) await openInvoice(inv);
    } catch (e) {
      setError(`Re-parse failed: ${e.message}`);
    }
  };

  // Every branch the caller can see, not only those that happen to have an
  // invoice. Deriving this from the invoice list meant that when nothing had
  // arrived the filter was empty too - which reads as "there are no
  // pharmacies" rather than "there are no invoices yet".
  const pharmacies = useMemo(() => {
    const fromBranches = branchList.map(b => b.pharmacyName).filter(Boolean);
    const fromInvoices = invoices.map(i => i.pharmacyName).filter(Boolean);
    return [...new Set([...fromBranches, ...fromInvoices])].sort();
  }, [branchList, invoices]);
  const suppliers = useMemo(() => [...new Set(invoices.map(i => i.supplierName).filter(Boolean))].sort(), [invoices]);

  /* Search and the dropdowns apply ACROSS the tabs, not within one.
   *
   * So the counts on the tabs are counts of what the search found, and a
   * search for an invoice number tells you which tab it is on rather than
   * returning nothing because you were looking at the wrong one. */
  const searched = useMemo(() => {
    const q = search.trim().toLowerCase();
    return invoices.filter(i => {
      if (pharmacyFilter && i.pharmacyName !== pharmacyFilter) return false;
      if (supplierFilter && i.supplierName !== supplierFilter) return false;
      if (!q) return true;
      return [i.invoiceNumber, i.supplierName, i.pharmacyName].filter(Boolean).some(v => String(v).toLowerCase().includes(q));
    });
  }, [invoices, pharmacyFilter, supplierFilter, search]);
  const counts = useMemo(() => {
    const c = Object.fromEntries(BUCKETS.map(b => [b.key, 0]));
    for (const i of searched) c[bucketOf(i)] += 1;
    return c;
  }, [searched]);
  const filtered = useMemo(() => searched.filter(i => bucketOf(i) === tab), [searched, tab]);
  const fmtDate = iso => {
    if (!iso) return "-";
    const d = new Date(iso);
    return isNaN(d) ? "-" : d.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric"
    });
  };
  const money = pence => pence == null ? "-" : `£${(Number(pence) / 100).toFixed(2)}`;
  const statusChip = inv => {
    const map = {
      needsReview: ["#fff4e5", "#a05a00", "needs review"],
      awaiting: ["#eef4ff", "#1c4587", "awaiting goods"],
      complete: ["#e8f5e9", "#177245", "complete"],
      discrepancy: ["#fdecea", "#a00", "discrepancy"],
      open: ["#eef4ff", "#1c4587", "in progress"],
      closed: ["#e8f5e9", "#177245", "closed"]
    };
    const [bg, fg, label] = map[inv.status] || ["#f0f0f0", "#555", inv.status || "?"];
    return <span style={{
      background: bg,
      color: fg,
      border: `1px solid ${fg}44`,
      borderRadius: 3,
      padding: "1px 6px",
      fontSize: "0.8em"
    }}>
        {label}
      </span>;
  };
  const panel = {
    border: "1px solid #ccc",
    marginBottom: 14,
    background: "#fff"
  };
  const panelHead = {
    padding: "8px 10px",
    background: "#f6f8fa",
    borderBottom: "1px solid #ddd",
    fontWeight: "bold",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center"
  };
  const th = {
    textAlign: "left",
    padding: 7,
    fontSize: "0.9em"
  };
  const td = {
    padding: 7,
    fontSize: "0.9em"
  };
  return <div style={{
    padding: 16
  }}>
      <div style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between"
    }}>
        <h2 style={{
        margin: 0
      }}>Invoices</h2>
        <MaskToggleButton />
      </div>

      <div style={{
      display: "flex",
      gap: 4,
      margin: "10px 0",
      borderBottom: "2px solid #F0F0F0"
    }}>
        {[["invoices", "Invoices"], ["analysis", "Invoice analysis"], ["setup", "Suppliers & accounts"]].map(([k, label]) => <button key={k} onClick={() => setMainTab(k)} style={{
        padding: "7px 14px",
        cursor: "pointer",
        border: 0,
        background: "transparent",
        borderBottom: mainTab === k ? "3px solid #0958D9" : "3px solid transparent",
        fontWeight: mainTab === k ? "bold" : "normal",
        color: mainTab === k ? "#0958D9" : "#434343",
        fontSize: 14
      }}>
            {label}
          </button>)}
      </div>

      {mainTab === "analysis" && <InvoiceAnalysis />}
      {mainTab === "setup" && <InvoiceSuppliers />}

      {mainTab === "invoices" && <>
      <p style={{
        fontSize: "0.9em",
        color: "#555",
        marginTop: 6,
        maxWidth: 920
      }}>
        Supplier invoices forwarded in by branch, matched against what was
        actually scanned onto the shelves. Received figures come from the
        snapshot made at booking-in, so they do not change when stock is later
        split, transferred or destroyed.
      </p>

      <div style={{
        margin: "12px 0"
      }}>
        <select value={pharmacyFilter} onChange={e => setPharmacyFilter(e.target.value)} style={{
          padding: 6,
          marginRight: 10,
          minWidth: 180
        }}>
          <option value="">All pharmacies</option>
          {pharmacies.map(p => <option key={p} value={p}>{displayName(p, masked)}</option>)}
        </select>

        <select value={supplierFilter} onChange={e => setSupplierFilter(e.target.value)} style={{
          padding: 6,
          marginRight: 10,
          minWidth: 180
        }}>
          <option value="">All suppliers</option>
          {suppliers.map(s => <option key={s} value={s}>{s}</option>)}
        </select>

        <input type="search" value={search} onChange={e => setSearch(e.target.value)} placeholder="Invoice number, supplier or branch" style={{
          padding: 6,
          minWidth: 260
        }} />

        <span style={{
          color: "#666",
          fontSize: "0.9em",
          marginLeft: 10
        }}>
          {searched.length} invoice{searched.length === 1 ? "" : "s"}
          {search.trim() && " found"}
        </span>
      </div>

      {/* The six states, in the order the work happens. Every invoice is on
          exactly one, so these counts add up to the number above - which is
          what makes it safe to work through a tab and know you are done. */}
      <div style={{
        display: "flex",
        flexWrap: "wrap",
        gap: 6,
        borderBottom: "2px solid #F0F0F0",
        marginBottom: 12
      }}>
        {BUCKETS.map(b => {
          const on = tab === b.key;
          const n = counts[b.key] || 0;
          return <button key={b.key} onClick={() => {
            setTab(b.key);
            // Clear the open invoice: it belongs to the tab being left, and
            // leaving its detail on screen beside a different list reads as
            // though it were on this tab too.
            setSelectedId(null);
            setRecon(null);
            setReferral(null);
            setResolution("");
          }} title={b.hint} style={{
            border: "1px solid #F0F0F0",
            borderBottom: "none",
            borderRadius: "5px 5px 0 0",
            padding: "7px 12px",
            cursor: "pointer",
            fontSize: "0.92em",
            background: on ? "#fff" : "#FAFAFA",
            fontWeight: on ? "bold" : "normal",
            // The two that need somebody to act are coloured even when the
            // tab is not selected. A number sitting quietly in grey is how
            // a variance stays unreferred for a fortnight.
            color: b.urgent && n > 0 ? "#a05a00" : "#262626",
            boxShadow: on ? "0 2px 0 0 #fff" : "none",
            marginBottom: on ? -2 : 0
          }}>
              {b.label}
              <span style={{
              marginLeft: 6,
              padding: "0 6px",
              borderRadius: 9,
              fontSize: "0.85em",
              background: b.urgent && n > 0 ? "#fff4e5" : "#eef2f6",
              color: b.urgent && n > 0 ? "#a05a00" : "#5D5D5D"
            }}>{n}</span>
            </button>;
        })}
      </div>

      {error && <div style={{
        background: "#fff4f4",
        border: "1px solid #e0b4b4",
        padding: "8px 10px",
        marginBottom: 10
      }}>{error}</div>}

      <div style={{
        display: "flex",
        gap: 16,
        alignItems: "flex-start"
      }}>

        {/* ------------------------------------------------ left: invoices */}
        <div style={{
          flex: "0 0 320px",
          border: "1px solid #ccc",
          maxHeight: "76vh",
          overflowY: "auto"
        }}>
          {loadingList && <div style={{
            padding: 12
          }}>Loading…</div>}
          {!loadingList && filtered.length === 0 && <div style={{
            padding: 12,
            color: "#666",
            fontSize: "0.92em"
          }}>
              {search.trim() ? "Nothing on this tab matches that search — try another tab." : (BUCKETS.find(b => b.key === tab) || {}).hint}
            </div>}
          {filtered.map(inv => {
            const on = selectedId === inv._id;
            return <div key={inv._id} onClick={() => openInvoice(inv)} style={{
              padding: "8px 10px",
              borderBottom: "1px solid #eee",
              cursor: "pointer",
              background: on ? "#e8f4ef" : "transparent",
              borderLeft: on ? "4px solid #177245" : "4px solid transparent"
            }}>
                <div style={{
                fontWeight: "bold"
              }}>
                  {inv.supplierName || <span style={{
                  color: "#a05a00"
                }}>Unknown supplier</span>}
                </div>
                <div style={{
                fontSize: "0.9em"
              }}>
                  {inv.invoiceNumber || <span style={{
                  color: "#888"
                }}>no number</span>}
                  {" · "}{fmtDate(inv.invoiceDate || inv.created)}
                </div>
                <div style={{
                fontSize: "0.85em",
                color: "#666",
                marginTop: 2
              }}>
                  {displayName(inv.pharmacyName, masked) || "unassigned"}
                </div>
                <div style={{
                marginTop: 4
              }}>
                  {statusChip(inv)}
                  {/* A photographed invoice is created before anything has been
                      read off the image, so "0 expected" means "not yet" as
                      often as it means "nothing". Without this the two are
                      indistinguishable from the list. */}
                  {["pending", "processing"].includes(inv.extraction?.state) && <span style={{
                  marginLeft: 6,
                  background: "#eef4ff",
                  color: "#1c4587",
                  border: "1px solid #1c458744",
                  borderRadius: 3,
                  padding: "1px 6px",
                  fontSize: "0.8em"
                }}>
                      reading photo
                    </span>}
                  {inv.extraction?.state === "failed" && <span style={{
                  marginLeft: 6,
                  background: "#fdecea",
                  color: "#a00",
                  border: "1px solid #a0000044",
                  borderRadius: 3,
                  padding: "1px 6px",
                  fontSize: "0.8em"
                }}>
                      photo unreadable
                    </span>}
                  {/* Part of a multi-invoice tote. Marked in the list so four
                      rows from one delivery are not read as four separate
                      deliveries that each came up short. */}
                  {inv.deliveryId && <span style={{
                  marginLeft: 6,
                  background: "#eef2ff",
                  color: "#1D39C4",
                  border: "1px solid #3730a344",
                  borderRadius: 3,
                  padding: "1px 6px",
                  fontSize: "0.8em"
                }}>
                      one of a delivery
                    </span>}
                  {/* Where this sits in the variance workflow. On the list
                      because a closed invoice waiting on a supplier and a
                      closed invoice with nothing wrong are otherwise the same
                      grey row. */}
                  {inv.varianceStatus && VARIANCE[inv.varianceStatus] && <span title={VARIANCE[inv.varianceStatus].hint} style={{
                  marginLeft: 6,
                  background: VARIANCE[inv.varianceStatus].bg,
                  color: VARIANCE[inv.varianceStatus].fg,
                  border: `1px solid ${VARIANCE[inv.varianceStatus].fg}44`,
                  borderRadius: 3,
                  padding: "1px 6px",
                  fontSize: "0.8em"
                }}>
                      {VARIANCE[inv.varianceStatus].label}
                    </span>}
                  {inv.possibleDuplicateOf && <span style={{
                  marginLeft: 6,
                  background: "#fff4e5",
                  color: "#a05a00",
                  border: "1px solid #a05a0044",
                  borderRadius: 3,
                  padding: "1px 6px",
                  fontSize: "0.8em"
                }}>
                      possible duplicate
                    </span>}
                  <span style={{
                  marginLeft: 6,
                  fontSize: "0.8em",
                  color: "#666"
                }}>
                    {inv.expectedLineCount || 0} expected · {inv.itemCount || 0} scanned
                  </span>
                </div>
              </div>;
          })}
        </div>

        {/* ------------------------------------------------- right: detail */}
        <div style={{
          flex: 1,
          maxHeight: "76vh",
          overflowY: "auto"
        }}>
          {loadingDetail && <div style={{
            padding: 12
          }}>Loading…</div>}

          {!loadingDetail && !recon && <div style={{
            padding: 16,
            color: "#666",
            border: "1px solid #ccc"
          }}>
              Select an invoice.
            </div>}

          {!loadingDetail && recon && <>
              {(() => {
              /* ============================================================
               * HOW THIS INVOICE IS DOING, IN ONE COLOUR
               * ============================================================
               * Three states, not two. "Short" and "fine" would paint every
               * not-yet-started invoice amber - flagging as a problem
               * something that has simply not been done yet - so an invoice
               * nobody has scanned against stays neutral until there is
               * something to judge.
               *
               * Damage counts even when the arithmetic is perfect: five packs
               * arrived, five were scanned, and two of them cannot be
               * dispensed. That delivery matched the paperwork and is still
               * wrong, and it is exactly the case that used to close as
               * complete and never get chased.
               * ========================================================== */
              const s = recon.summary || {};
              const anythingBookedIn = (recon.scanned || []).length > 0;
              const bad = s.outstandingLines > 0 || s.flaggedLines > 0 || s.unexpectedLines > 0 || s.overLines > 0;
              const tone = !anythingBookedIn ? {
                bg: "#FAFAFA",
                edge: "#D9D9D9",
                text: "#5D5D5D",
                label: "Not booked in yet"
              } : bad ? {
                bg: "#fdf6e3",
                edge: "#c8a44a",
                text: "#7a5a00",
                label: "Variance"
              } : {
                bg: "#eef7ee",
                edge: "#8ab98a",
                text: "#1c5b1c",
                label: "All received, nothing flagged"
              };
              const st = recon.invoice.varianceStatus;
              const v = VARIANCE[st];

              // Damaged and short-dated columns appear only where there are
              // any. On a clean invoice they would be two columns of zeros
              // pushing the drug name off a narrow screen.
              const anyDamaged = (s.damagedPacks || 0) > 0;
              const anyShortDated = (s.shortDatedPacks || 0) > 0;
              const money = p => p == null ? "—" : `£${(p / 100).toFixed(2)}`;
              const lineValue = (recon.expected || []).reduce((n, l) => n + (l.lineTotalPence || 0), 0);
              const isPhoto = (recon.invoice.pageCount || 0) > 0 || recon.invoice.source === "photo";

              /** Everything known about a line, for the hover.
               *  Codes and match provenance live here rather than in columns
               *  - they matter when a line looks wrong and are noise the rest
               *  of the time. */
              const lineTitle = l => [l.pip && `PIP ${l.pip}`, l.gtin && `Barcode ${l.gtin}`, l.supplierProductCode && `Supplier code ${l.supplierProductCode}`, l.packSizeText && `Pack as printed: ${l.packSizeText}`, l.dmdName && `dm+d: ${l.dmdName}`, l.matchedBy && l.matchedBy !== "none" && `Matched by ${l.matchedBy === "gtin" ? "barcode" : l.matchedBy}` + (l.matchedName ? ` to ${l.matchedName}` : ""), l.matchedBy === "none" && l.receivedBy !== "hand" && "Nothing scanned has matched this line",
              /* How the figure was arrived at, in words.
               *
               * A hand-checked line and a scanned line used to be
               * indistinguishable here, which mattered most in exactly the
               * situation this screen exists for: arguing a variance with
               * a wholesaler three weeks later, where "the scanner counted
               * nine" and "somebody said nine" are not the same claim. */
              l.receivedBy === "hand" && (() => {
                const c = l.checkedOff;
                if (!c) return "Received figure set by hand";
                const where = c.source === "office" ? "from the office" : "in branch";
                const who = c.by ? ` by ${c.by}` : "";
                const when = c.at ? ` on ${new Date(c.at).toLocaleString()}` : "";
                const how = c.how === "tick" || c.how === "tick-all" ? "Ticked off by hand" : "Received figure entered by hand";
                return `${how} ${where}${who}${when}` + (l.scannedReceived > 0 && l.scannedReceived !== l.received ? ` — the scanner counted ${l.scannedReceived}` : "");
              })(), l.originalDescription && `The paper says: ${l.originalDescription}`, l.invoicedOverridden && "Invoiced figure corrected by hand"].filter(Boolean).join("\n");
              return <>
                    {/* ---- the state of play, and the paperwork trail ---- */}
                    <div style={{
                  background: tone.bg,
                  border: `1px solid ${tone.edge}`,
                  padding: "8px 10px",
                  marginBottom: 12
                }}>
                      <div style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    flexWrap: "wrap"
                  }}>
                        <strong style={{
                      color: tone.text
                    }}>{tone.label}</strong>
                        <span style={{
                      fontSize: "0.9em",
                      color: tone.text
                    }}>
                          {s.completeLines}/{s.expectedLines} lines received
                          {/* How, not just how many. A delivery ratified
                              entirely by hand is complete and valid - but
                              "0 scanned" on a closed invoice otherwise reads
                              as though nothing happened at all. */}
                          {s.handCheckedLines > 0 && ` · ${s.handCheckedLines} checked off by hand`}
                          {s.unconfirmedLines > 0 && ` · ${s.unconfirmedLines} not confirmed either way`}
                          {s.outstandingLines > 0 && ` · ${s.outstandingLines} short`}
                          {s.overLines > 0 && ` · ${s.overLines} over`}
                          {s.unexpectedLines > 0 && ` · ${s.unexpectedLines} not on the invoice`}
                          {s.damagedPacks > 0 && ` · ${s.damagedPacks} damaged`}
                          {s.shortDatedPacks > 0 && ` · ${s.shortDatedPacks} short dated`}
                        </span>
                        <span style={{
                      flex: 1
                    }} />

                        {/* The extraction's own complaints, folded away. They
                            are long, they are only about how the photograph
                            read, and they were taking the top third of the
                            screen on every invoice. */}
                        {recon.invoice.problems?.length > 0 && <button onClick={() => setProblemsOpen(!problemsOpen)} title="What the reader could not be sure about">
                            {problemsOpen ? "▾" : "▸"} Reading notes
                            {" "}({recon.invoice.problems.length})
                          </button>}
                        {v && <span title={v.hint} style={{
                      background: v.bg,
                      color: v.fg,
                      border: `1px solid ${v.fg}44`,
                      borderRadius: 3,
                      padding: "1px 6px",
                      fontSize: "0.85em"
                    }}>
                            {v.label}
                          </span>}
                        {st === "UNRESOLVED" && !referral && <button disabled={varianceBusy} onClick={() => setReferral({
                      method: "phone",
                      at: new Date().toISOString().slice(0, 10),
                      reference: ""
                    })}>Record a referral</button>}
                      </div>

                      {problemsOpen && recon.invoice.problems?.length > 0 && <div style={{
                    marginTop: 8
                  }}>
                          <ul style={{
                      margin: "4px 0 8px 18px",
                      fontSize: "0.9em"
                    }}>
                            {recon.invoice.problems.map((p, i) => <li key={i}>{p}</li>)}
                          </ul>
                          <button onClick={() => reparse(recon.invoice._id)}>
                            Try parsing again
                          </button>
                        </div>}

                      {/* ---- referral: how and when, not just that ---- */}
                      {referral && st === "UNRESOLVED" && <div style={{
                    marginTop: 8,
                    padding: 8,
                    background: "#fff",
                    border: "1px solid #F0F0F0"
                  }}>
                          <div style={{
                      display: "flex",
                      gap: 8,
                      flexWrap: "wrap",
                      alignItems: "center",
                      marginBottom: 6
                    }}>
                            <strong style={{
                        fontSize: "0.9em"
                      }}>How was it referred?</strong>
                            <select value={referral.method} onChange={e => setReferral({
                        ...referral,
                        method: e.target.value
                      })}>
                              {REFERRAL_METHODS.map(([val, l]) => <option key={val} value={val}>{l}</option>)}
                            </select>
                            <input type="date" value={referral.at} onChange={e => setReferral({
                        ...referral,
                        at: e.target.value
                      })} />
                          </div>
                          <input value={referral.reference} onChange={e => setReferral({
                      ...referral,
                      reference: e.target.value
                    })} placeholder={referral.method === "phone" ? "Who you spoke to, and what they said" : referral.method === "portal" ? "Case or ticket number" : "Reference, or what you told them"} style={{
                      width: "100%",
                      padding: 5,
                      marginBottom: 6
                    }} />
                          <button disabled={varianceBusy} onClick={() => setVariance(recon.invoice._id, "SUPPLIER_EMAILED", {
                      referralMethod: referral.method,
                      referralAt: referral.at ? new Date(referral.at).toISOString() : new Date().toISOString(),
                      referralRef: referral.reference || null
                    })}>
                            {varianceBusy ? "Saving…" : "Save — now awaiting resolution"}
                          </button>
                          <button disabled={varianceBusy} style={{
                      marginLeft: 6
                    }} onClick={() => setReferral(null)}>Cancel</button>
                        </div>}

                      {recon.invoice.varianceReferral && <div style={{
                    marginTop: 6,
                    fontSize: "0.85em",
                    color: tone.text
                  }}>
                          <b>Referred:</b>{" "}
                          {(REFERRAL_METHODS.find(([val]) => val === recon.invoice.varianceReferral.method) || [])[1] || recon.invoice.varianceReferral.method}
                          {" · "}{fmtDate(recon.invoice.varianceReferral.at)}
                          {recon.invoice.varianceReferral.reference && ` · ${recon.invoice.varianceReferral.reference}`}
                        </div>}

                      {st === "SUPPLIER_EMAILED" && <div style={{
                    marginTop: 8,
                    padding: 8,
                    background: "#fff",
                    border: "1px solid #F0F0F0"
                  }}>
                          <strong style={{
                      fontSize: "0.9em"
                    }}>How was it resolved?</strong>
                          <textarea value={resolution} rows={2} onChange={e => setResolution(e.target.value)} placeholder="Credit note number, replacement sent, written off, no claim…" style={{
                      width: "100%",
                      padding: 5,
                      margin: "6px 0"
                    }} />
                          {/* Required. "Resolved" with nothing behind it is
                              indistinguishable from clearing a tab to tidy it. */}
                          <button disabled={varianceBusy || !resolution.trim()} title={resolution.trim() ? undefined : "Say what happened before marking it resolved"} onClick={() => setVariance(recon.invoice._id, "RESOLVED", {
                      resolutionOutcome: resolution.trim()
                    })}>
                            {varianceBusy ? "Saving…" : "Mark resolved"}
                          </button>
                        </div>}

                      {recon.invoice.varianceResolution && <div style={{
                    marginTop: 6,
                    fontSize: "0.85em",
                    color: tone.text
                  }}>
                          <b>Resolved</b> {fmtDate(recon.invoice.varianceResolution.at)}
                          {" — "}{recon.invoice.varianceResolution.outcome}
                        </div>}

                      {recon.invoice.possibleDuplicateOf && <div style={{
                    marginTop: 6,
                    fontSize: "0.85em",
                    color: "#a05a00"
                  }}>
                          Invoice {recon.invoice.invoiceNumber || "(no number)"} is already on
                          file for this branch — check both before closing either.{" "}
                          <button onClick={() => {
                      const twin = invoices.find(i => i._id === recon.invoice.possibleDuplicateOf);
                      if (twin) openInvoice(twin);else setError("The other copy is not on this tab — try another.");
                    }}>Open the other copy</button>
                        </div>}

                      {recon.delivery && recon.delivery.invoices.length > 1 && <div style={{
                    marginTop: 6,
                    fontSize: "0.85em",
                    color: tone.text
                  }}>
                          <b>One of {recon.delivery.invoices.length} invoices in this tote.</b>{" "}
                          {recon.delivery.invoices.filter(d => !d.isThisOne).map(d => <button key={d.invoiceId} style={{
                      marginRight: 4
                    }} onClick={() => {
                      const t = invoices.find(i => i._id === d.invoiceId);
                      if (t) openInvoice(t);else setError("That invoice is not on this tab — try another.");
                    }}>
                              {d.supplierName || "?"} {d.invoiceNumber || "(no number)"}
                            </button>)}
                        </div>}
                    </div>

                    {/* ================= the document, and the goods =========
                     * Side by side because the whole job is comparing one with
                     * the other. Stacked, checking a line meant scrolling past
                     * the picture of the thing you were checking it against.
                     * ==================================================== */}
                    <div style={{
                  display: "flex",
                  gap: 12,
                  alignItems: "flex-start",
                  flexWrap: "wrap"
                }}>

                      {/* ---- left: however this invoice arrived ---- */}
                      <div style={{
                    ...panel,
                    flex: "1 1 380px",
                    minWidth: 320,
                    marginBottom: 0
                  }}>
                        <div style={panelHead}>
                          <span>
                            {isPhoto ? `Photographed invoice${recon.invoice.pageCount > 1 ? ` (${recon.invoice.pageCount} pages)` : ""}` : "Invoice email"}
                          </span>
                          <span style={{
                        display: "flex",
                        gap: 6,
                        alignItems: "center"
                      }}>
                            {isPhoto && recon.invoice.extraction?.state && <span style={{
                          fontWeight: "normal",
                          fontSize: "0.8em",
                          color: "#5D5D5D"
                        }}>
                                {recon.invoice.extraction.state}
                              </span>}
                            {isPhoto && <button disabled={retrying} onClick={() => retryExtraction(recon.invoice._id)}>
                                {retrying ? "Queueing…" : "Read again"}
                              </button>}
                            {!isPhoto && recon.invoice.source === "email" && <button onClick={() => setEmailOpen(!emailOpen)}>
                                {emailOpen ? "Collapse" : "Expand to read"}
                              </button>}
                          </span>
                        </div>

                        <div style={{
                      padding: 10
                    }}>
                          {isPhoto ? pages.length === 0 ? <span style={{
                        color: "#666",
                        fontSize: "0.9em"
                      }}>
                                {recon.invoice.pageCount > 0 ? "Loading pages…" : "No pages stored."}
                              </span> : <div style={{
                        display: "flex",
                        gap: 8,
                        flexWrap: "wrap"
                      }}>
                                {pages.map(pg => <img key={pg._id} src={`data:${pg.contentType};base64,${pg.imageBase64}`} alt={`Invoice page ${pg.page}`} onClick={() => setLightbox({
                          imageBase64: pg.imageBase64,
                          caption: `Invoice page ${pg.page}`,
                          takenAt: pg.takenAt
                        })}
                        // Big enough to read a column heading off,
                        // because that is what it is for. Click to
                        // enlarge properly.
                        style={{
                          width: "100%",
                          maxHeight: 620,
                          objectFit: "contain",
                          cursor: "zoom-in",
                          border: "1px solid #ddd"
                        }} />)}
                              </div> : recon.invoice.source !== "email" ? <span style={{
                        color: "#666"
                      }}>
                              Raised in the app during goods-in — there is no email
                              and no photograph.
                            </span> : <>
                              <div style={{
                          fontSize: "0.85em",
                          color: "#666",
                          marginBottom: 6
                        }}>
                                <strong>{recon.invoice.emailSubject || "(no subject)"}</strong><br />
                                from {recon.invoice.emailFrom || "?"} to {recon.invoice.emailTo || "?"}
                              </div>
                              {emailOpen && <pre style={{
                          whiteSpace: "pre-wrap",
                          fontSize: "0.8em",
                          maxHeight: 560,
                          overflow: "auto",
                          background: "#fafafa",
                          padding: 8
                        }}>
                                  {recon.invoice.emailText || "(no text stored)"}
                                </pre>}
                            </>}
                        </div>
                      </div>

                      {/* ---- right: what was invoiced, against what came ---- */}
                      <div style={{
                    ...panel,
                    flex: "1 1 420px",
                    minWidth: 340,
                    marginBottom: 0
                  }}>
                        <div style={panelHead}>
                          <span>Invoiced items</span>
                          <span style={{
                        fontWeight: "normal",
                        fontSize: "0.85em",
                        color: "#555"
                      }}>
                            hover a line for codes
                          </span>
                        </div>

                        {recon.expected.length === 0 && recon.unexpected.length === 0 ? <div style={{
                      padding: 12,
                      color: "#666"
                    }}>
                            {["pending", "processing"].includes(recon.invoice.extraction?.state) ? "The photographed invoice has not finished being read — " + "this list has not arrived yet." : recon.invoice.extraction?.state === "failed" ? "The photograph could not be read, so there are no lines." : "No invoice lines."}
                          </div> : <table style={{
                      width: "100%",
                      borderCollapse: "collapse"
                    }}>
                            <thead>
                              <tr style={{
                          background: "#fafafa"
                        }}>
                                <th style={th}>Item</th>
                                <th style={{
                            ...th,
                            textAlign: "right"
                          }}>Sent</th>
                                <th style={{
                            ...th,
                            textAlign: "right"
                          }}>Received</th>
                                {anyDamaged && <th style={{
                            ...th,
                            textAlign: "right"
                          }}>Dmgd</th>}
                                {anyShortDated && <th style={{
                            ...th,
                            textAlign: "right"
                          }}>Short&nbsp;dated</th>}
                                <th style={{
                            ...th,
                            textAlign: "right"
                          }}>Value</th>
                              </tr>
                            </thead>
                            <tbody>
                              {/* In the tote, on no line. At the top, because it
                                  is the thing somebody has to resolve before the
                                  rest of the grid means anything. */}
                              {recon.unexpected.map((u, i) => <tr key={`x${i}`} style={{
                          background: "#f3e8ff"
                        }}>
                                  <td style={{
                            ...td,
                            color: "#6b21a8"
                          }}>
                                    {u.vmppNm || u.amppNm || "(unnamed)"}
                                    <div style={{
                              fontSize: "0.78em"
                            }}>
                                      in the tote — not on any line of this invoice
                                    </div>
                                  </td>
                                  <td style={{
                            ...td,
                            textAlign: "right",
                            color: "#6b21a8"
                          }}>—</td>
                                  <td style={{
                            ...td,
                            textAlign: "right",
                            color: "#6b21a8",
                            fontWeight: "bold"
                          }}>{u.packets}</td>
                                  {anyDamaged && <td style={td} />}
                                  {anyShortDated && <td style={td} />}
                                  <td style={{
                            ...td,
                            textAlign: "right"
                          }}>—</td>
                                </tr>)}

                              {recon.expected.map((l, i) => {
                          const dmg = (l.issuePacks || {}).damaged || 0;
                          const sd = (l.issuePacks || {}).shortDated || 0;
                          const shortfall = l.outstanding > 0;
                          return <tr key={i} title={lineTitle(l)} style={{
                            borderTop: "1px solid #F5F5F5",
                            background: shortfall ? "#fdf6e3" : dmg || sd ? "#fffaf0" : "transparent"
                          }}>
                                    <td style={td}>
                                      {l.description || "(no description)"}
                                      {/* How many packs on this line were
                                          photographed. On the invoiced grid as
                                          well as the scan list because this is
                                          the grid somebody works a variance
                                          from - the evidence should be one
                                          click from the line it supports. */}
                                      {(() => {
                                const shots = (recon.scanned || []).filter(sc => sc.itemId && (recon.photoItemIds || []).includes(String(sc.itemId)) && (sc.vmppNm || sc.amppNm || "").toLowerCase().trim() === (l.matchedName || "").toLowerCase().trim());
                                if (!shots.length) return null;
                                return <button title={`${shots.length} photograph${shots.length === 1 ? "" : "s"} of the packs booked in against this line`} onClick={() => openPackPhoto(recon.invoice._id, shots[0].itemId, l.description || "pack")} style={{
                                  marginLeft: 6,
                                  padding: "0 4px",
                                  cursor: "pointer"
                                }}>
                                            📷{shots.length > 1 ? ` ${shots.length}` : ""}
                                          </button>;
                              })()}
                                      {l.dmdName && l.dmdName !== l.description && <div style={{
                                fontSize: "0.78em",
                                color: "#177245"
                              }}>
                                          {l.dmdName}
                                        </div>}
                                      {l.packSizeText && <div style={{
                                fontSize: "0.78em",
                                color: "#8C8C8C"
                              }}>
                                          pack {l.packSizeText}
                                        </div>}
                                    </td>
                                    <td style={{
                              ...td,
                              textAlign: "right"
                            }}>{l.quantity}</td>
                                    <td style={{
                              ...td,
                              textAlign: "right",
                              fontWeight: shortfall ? "bold" : "normal",
                              color: shortfall ? "#a05a00" : l.over > 0 ? "#0958D9" : "inherit"
                            }}>
                                      <span style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 4,
                                justifyContent: "flex-end"
                              }}>
                                        {/* Ticked off, or counted. The figure
                                            alone does not say which, and the
                                            two are different strengths of
                                            evidence when a variance is being
                                            argued with a supplier. */}
                                        {l.receivedBy === "hand" && <span style={{
                                  fontSize: "0.72em",
                                  color: "#0E8CFF",
                                  background: "#E6F7FF",
                                  padding: "0 4px",
                                  borderRadius: 3
                                }}>
                                            by hand
                                          </span>}
                                        {l.received}
                                        {/* Nothing to tick where the scanner
                                            has already satisfied the line -
                                            replacing a scan with a tick trades
                                            better evidence for worse. */}
                                        {(l.receivedBy === "hand" || !l.complete) && <button disabled={checkingLine === l.key} onClick={() => checkOffLine(recon.invoice._id, l)} title={l.receivedBy === "hand" ? "Undo this — hand the line back to the scan count" : `Tick this line off as fully received (${l.quantity})`} style={{
                                  cursor: "pointer",
                                  border: "1px solid #D9D9D9",
                                  background: l.receivedBy === "hand" ? "#E6F7FF" : "#fff",
                                  borderRadius: 3,
                                  padding: "0 5px",
                                  fontSize: "0.85em",
                                  lineHeight: "1.5"
                                }}>
                                            {checkingLine === l.key ? "…" : l.receivedBy === "hand" ? "↺" : "✓"}
                                          </button>}
                                      </span>
                                      {shortfall && <div style={{
                                fontSize: "0.78em",
                                fontWeight: "normal"
                              }}>
                                          {l.outstanding} short
                                        </div>}
                                    </td>
                                    {anyDamaged && <td style={{
                              ...td,
                              textAlign: "right",
                              color: dmg ? "#a05a00" : "#D9D9D9"
                            }}>
                                        {dmg || "—"}
                                      </td>}
                                    {anyShortDated && <td style={{
                              ...td,
                              textAlign: "right",
                              color: sd ? "#a05a00" : "#D9D9D9"
                            }}>
                                        {sd || "—"}
                                      </td>}
                                    <td style={{
                              ...td,
                              textAlign: "right"
                            }}>
                                      {money(l.lineTotalPence)}
                                    </td>
                                  </tr>;
                        })}
                            </tbody>
                            <tfoot>
                              <tr style={{
                          background: "#fafafa",
                          borderTop: "2px solid #F0F0F0"
                        }}>
                                <td style={{
                            ...td,
                            fontWeight: "bold"
                          }}>
                                  Invoice value
                                </td>
                                <td style={td} />
                                <td style={td} />
                                {anyDamaged && <td style={td} />}
                                {anyShortDated && <td style={td} />}
                                <td style={{
                            ...td,
                            textAlign: "right",
                            fontWeight: "bold"
                          }}>
                                  {money(lineValue)}
                                </td>
                              </tr>
                            </tfoot>
                          </table>}
                      </div>
                    </div>

                    {/* ---- the box as it arrived ----
                        A small strip, kept because damage is now a claim
                        against the supplier and a photograph of the tote is
                        the evidence for it. Thumbnails only: it is a footnote
                        to the comparison above, not part of it. */}
                    {photos.length > 0 && <div style={{
                  ...panel,
                  marginTop: 12,
                  marginBottom: 0
                }}>
                        <div style={panelHead}>
                          <span>The box as it arrived ({photos.length})</span>
                        </div>
                        <div style={{
                    padding: 8,
                    display: "flex",
                    gap: 6,
                    flexWrap: "wrap"
                  }}>
                          {photos.map((ph, i) => ph.imageBase64 ? <img key={i} src={`data:image/jpeg;base64,${ph.imageBase64}`} alt={ph.caption || `Box photo ${i + 1}`} onClick={() => setLightbox(ph)} style={{
                      width: 76,
                      height: 76,
                      objectFit: "cover",
                      cursor: "zoom-in",
                      border: "1px solid #ddd"
                    }} /> : null)}
                        </div>
                      </div>}

                    <div style={{
                  height: 14
                }} />
                  </>;
            })()}

              {/* ---- 3. the raw scans ---- */}
              <div style={panel}>
                <div style={panelHead}>
                  <span>Scanned items</span>
                  <span style={{
                  fontWeight: "normal",
                  fontSize: "0.85em",
                  color: "#555"
                }}>
                    {recon.scanned.length} line{recon.scanned.length === 1 ? "" : "s"}
                  </span>
                </div>
                {recon.scanned.length === 0 ? <div style={{
                padding: 12,
                color: "#666"
              }}>
                    Nothing booked in against this invoice yet.
                  </div> : <table style={{
                width: "100%",
                borderCollapse: "collapse"
              }}>
                    <thead>
                      <tr style={{
                    background: "#fafafa"
                  }}>
                        <th style={th}>Drug</th>
                        <th style={{
                      ...th,
                      textAlign: "right"
                    }}>Pack</th>
                        <th style={{
                      ...th,
                      textAlign: "right"
                    }}>Packs</th>
                        <th style={th}>Expiry</th>
                        <th style={th}>Batch</th>
                        <th style={th}>Shelf</th>
                        <th style={th}>Status</th>
                        <th style={th}>Saved</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recon.scanned.map((s, i) => <tr key={i} style={{
                    borderTop: "1px solid #eee"
                  }}>
                          <td style={td}>
                            {s.vmppNm || s.amppNm || "(unnamed)"}
                            {/* A photograph of THIS pack, taken as it was
                                booked in. The reason it is here rather than
                                only in the app: a damage claim is argued at
                                head office days later by somebody who was
                                never in the room. */}
                            {s.itemId && (recon.photoItemIds || []).includes(String(s.itemId)) && <button title="Photograph taken when this pack was booked in" onClick={() => openPackPhoto(recon.invoice._id, s.itemId, s.vmppNm || s.amppNm || "pack")} style={{
                        marginLeft: 6,
                        padding: "0 5px",
                        cursor: "pointer"
                      }}>
                                📷
                              </button>}
                            {/* How this packet came to be on this invoice.
                                "the barcode said so" and "two invoices in the
                                tote listed it and this was the first" are
                                different answers, and only one is arbitrary. */}
                            {s.attributedBy === "name-ambiguous" && <div style={{
                        fontSize: "0.78em",
                        color: "#a05a00"
                      }} title="More than one invoice in this delivery listed this product. It was credited to the earliest, because nothing about the packet itself can say which was intended.">
                                also listed on another invoice in this delivery
                              </div>}
                            {s.unattributed && <div style={{
                        fontSize: "0.78em",
                        color: "#a05a00"
                      }}>
                                not on any invoice in this delivery
                              </div>}
                            {s.splitPack && <span style={{
                        color: "#a05a00",
                        fontSize: "0.85em"
                      }}>
                                {" "}(split)
                              </span>}
                          </td>
                          <td style={{
                      ...td,
                      textAlign: "right"
                    }}>{s.qtval || "-"}</td>
                          <td style={{
                      ...td,
                      textAlign: "right"
                    }}>{s.packets ?? "-"}</td>
                          <td style={td}>{s.expiryDate || "-"}</td>
                          <td style={td}>{s.batchNumber || "-"}</td>
                          <td style={td}>{s.shelfName || "-"}</td>
                          <td style={td}>{s.status || "-"}</td>
                          <td style={td}>{fmtDate(s.savedAt)}</td>
                        </tr>)}
                    </tbody>
                  </table>}
              </div>
            </>}
        </div>
      </div>
      </>}

      {packPhoto && <div onClick={() => setPackPhoto(null)} style={{
      position: "fixed",
      inset: 0,
      background: "rgba(0,0,0,0.85)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 320,
      cursor: "zoom-out"
    }}>
          <div style={{
        textAlign: "center",
        maxWidth: "92vw"
      }} onClick={e => e.stopPropagation()}>
            {packPhoto.loading ? <div style={{
          color: "#fff"
        }}>Loading…</div> : <>
                <img src={`data:image/jpeg;base64,${packPhoto.imageBase64}`} alt={packPhoto.label} style={{
            maxWidth: "92vw",
            maxHeight: "80vh",
            background: "#fff"
          }} />
                <div style={{
            color: "#fff",
            marginTop: 8,
            fontSize: "0.9em"
          }}>
                  {packPhoto.vmppNm || packPhoto.label}
                  {packPhoto.batchNumber && ` · batch ${packPhoto.batchNumber}`}
                  {packPhoto.expiryDate && ` · exp ${packPhoto.expiryDate}`}
                  {packPhoto.takenAt && ` · ${fmtDate(packPhoto.takenAt)}`}
                  <div style={{
              color: "#8C8C8C"
            }}>
                    Taken as this pack was booked in — click outside to close
                  </div>
                </div>
              </>}
          </div>
        </div>}

      {lightbox && <div onClick={() => setLightbox(null)} style={{
      position: "fixed",
      inset: 0,
      background: "rgba(0,0,0,0.8)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 200,
      cursor: "zoom-out"
    }}>
          <div style={{
        textAlign: "center"
      }}>
            <img src={`data:image/jpeg;base64,${lightbox.imageBase64}`} alt={lightbox.caption || "Delivery photograph"} style={{
          maxWidth: "90vw",
          maxHeight: "82vh",
          boxShadow: "0 6px 30px rgba(0,0,0,0.5)"
        }} />
            <div style={{
          color: "#fff",
          marginTop: 10,
          fontSize: "0.9em"
        }}>
              {lightbox.caption || "Box as delivered"}
              {lightbox.takenAt && ` · ${fmtDate(lightbox.takenAt)}`}
              <span style={{
            color: "#8C8C8C"
          }}> — click anywhere to close</span>
            </div>
          </div>
        </div>}
    </div>;
};
export default StockInvoices;
